import { useState, useEffect, useCallback } from 'react';
import { fetchAgents } from '../services/api';

/**
 * Custom hook for managing agents list
 * @returns {Object} Agents state and functions
 */
const useAgents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    agentId: ''
  });

  /**
   * Fetch agents from API
   */
  const loadAgents = useCallback(async (params = searchParams) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching agents with params:', params);
      const data = await fetchAgents(params);
      
      console.log('Agents fetched:', data);
      setAgents(data);
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError(err.message || 'Failed to load agents');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  /**
   * Update search parameters
   * @param {Object} newParams - New search parameters
   */
  const updateSearchParams = useCallback((newParams) => {
    setSearchParams(prev => ({
      ...prev,
      ...newParams
    }));
  }, []);

  /**
   * Clear all search parameters
   */
  const clearSearchParams = useCallback(() => {
    setSearchParams({
      keyword: '',
      agentId: ''
    });
  }, []);

  // Debounced search effect - 200ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      loadAgents(searchParams);
    }, 200);

    return () => clearTimeout(timer);
  }, [searchParams, loadAgents]);

  // Initial load
  useEffect(() => {
    loadAgents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    agents,
    loading,
    error,
    searchParams,
    updateSearchParams,
    clearSearchParams,
    refreshAgents: loadAgents
  };
};

export default useAgents;
